import { IS, IS_NOT_NIL, nil, NO, UIRectangle, UITableView, UIView, YES } from "uicore-ts"


export class RowView<CellType extends UIView = UIView> extends UIView {
    
    _previousLayoutBounds?: UIRectangle
    _cells: CellType[]
    _cellWeights: number[]
    
    padding = 0
    _cellWidths: number[] = []
    _cellWidthsAreExplicit = NO
    _cellMinimumWidths: number[] = []
    
    /**
     * An optional inset applied only to the left side of the first cell's frame.
     * This allows subclasses to reserve space for elements like expandability buttons
     * without fighting the base layout logic.
     */
    firstCellLeftInset = 0
    
    // _rowHeight?: number
    
    constructor(elementID?: string, cells: CellType[] = [], cellWidths: number[] = []) {
        
        super(elementID)
        
        this._cells = cells
        this._cellWeights = cellWidths
        
        this.usesVirtualLayoutingForIntrinsicSizing = NO
        
    }
    
    
    get cells() {
        return this._cells
    }
    
    set cells(cells: CellType[]) {
        
        const previousCells = this.cells
        const cellWeights = this.cellWeights.copy()
        
        
        // The cells are added to this._cells in this.addCell()
        
        previousCells.copy().forEach((cell: CellType, index: number) => {
            
            if (!cells.contains(cell)) {
                
                cell.removeFromSuperview()
                this._cells.removeElement(cell)
                
                cellWeights[index] = nil
                
            }
            
        })
        
        this.cellWeights = cellWeights.filter((cellWeight) => IS_NOT_NIL(cellWeight))
        
        cells.copy().forEach((cell: CellType, index: number) => {
            if (!IS(cell.superview)) {
                this.addCell(cell, 1, index)
            }
        })
        
        this._previousLayoutBounds = nil
        this.setNeedsLayout()
        
    }
    
    removeCellAtIndex(index: number) {
        const remainingCells = this.cells.copy()
        remainingCells.removeElementAtIndex(index)
        this.cellWeights.removeElementAtIndex(index)
        this.cells = remainingCells
    }
    
    removeFirstCell() {
        this.removeCellAtIndex(0)
    }
    
    removeLastCell() {
        this.removeCellAtIndex(this.cells.length - 1)
    }
    
    addCell(cell: CellType, weight: number = 1, index = this.cells.length) {
        
        if (this.cells.contains(cell) && IS_NOT_NIL(cell)) {
            return
        }
        
        this.cells.insertElementAtIndex(index, cell)
        this.cellWeights.insertElementAtIndex(index, weight)
        this.addSubview(cell)
        this.setNeedsLayout()
        
    }
    
    get cellWeights() {
        return this._cellWeights
    }
    
    set cellWeights(widths: number[]) {
        this._cellWeights = widths
        this.layoutParametersDidChange()
    }
    
    layoutParametersDidChange() {
        this._previousLayoutBounds = nil
        this.setNeedsLayout()
    }
    
    get cellWidths() {
        return this._cellWidths
    }
    
    set cellWidths(widths: number[]) {
        this._cellWidths = widths
        this._cellWidthsAreExplicit = widths.length > 0
        this.layoutParametersDidChange()
    }

    get cellMinimumWidths() {
        return this._cellMinimumWidths
    }

    set cellMinimumWidths(widths: number[]) {
        this._cellMinimumWidths = widths
        this.layoutParametersDidChange()
    }
    
    override get frame(): UIRectangle & { zIndex?: number } {
        return super.frame
    }
    
    override set frame(frame: UIRectangle & { zIndex?: number }) {
        const previousWidth = this._frame?.width
        super.frame = frame
        // Width changes invalidate derived widths, while descriptor-owned absolute widths must remain stable.
        if (frame.width !== previousWidth && !this._cellWidthsAreExplicit) {
            this.cellWidths = []
        }
    }
    
    get boundsHaveChangedSinceLayout() {
        return !this.bounds.isEqualTo(this._previousLayoutBounds)
    }
    
    // Returns the closest subview that is a UITableview
    get tableView(): UITableView | undefined {
        return this.withAllSuperviews.find(view => view.isKindOfClass(UITableView)) as UITableView | undefined
    }
    
    
    override layoutSubviews() {
        
        const bounds = this.contentBounds
        
        super.layoutSubviews()
        
        this._previousLayoutBounds = this.bounds
        
        let cellWidths = this.cellWidths

        if (cellWidths.length < this.cells.length && this.cells.length) {
            
            if (this.cellWeights.length >= this.cells.length) {
                cellWidths = [nil].arrayByRepeating(this.cells.length)
            }
            else {
                cellWidths = [
                    (bounds.width - this.padding * (this.cells.length - 1)) / this.cells.length
                ].arrayByRepeating(this.cells.length)
            }

            this._cellWidths = cellWidths
            
        }
        
        const widthFrames = bounds.rectanglesBySplittingWidth(
            this.cellWeights,
            this.padding,
            this._effectiveCellWidthsForBounds(bounds, cellWidths)
        )
        
        let height = bounds.height
        if (this.tableView?.allRowsHaveEqualHeight != YES) {
            height = [
                ...this.cells.map((cell, index) => {
                        if (widthFrames[index].width < 0) {
                            return 0
                        }
                        return cell.intrinsicContentHeight(widthFrames[index].width)
                    }
                ),
                bounds.height
            ].max()
        }
        
        this.cells.forEach((cell, index) => {
            let frame = widthFrames[index].rectangleWithHeight(height, 0.5)
            
            // Apply special narrowing to the first cell if requested
            if (index === 0 && this.firstCellLeftInset > 0) {
                frame = frame.rectangleWithInsets(this.firstCellLeftInset, 0, 0, 0)
            }
            
            cell.weakFrame = frame
            
        })
        
    }
    
    override intrinsicContentHeight(constrainingWidth?: number): number {
        
        const contentBounds = this.contentBounds
        const bounds = contentBounds.rectangleWithWidth(constrainingWidth ?? contentBounds.width)
        
        const lastRenderedWidth = this._previousLayoutBounds?.width
        const cellWidthsAreValidForThisQuery =
            this._cellWidths.length >= this.cells.length &&
            lastRenderedWidth !== undefined &&
            Math.abs(bounds.width - lastRenderedWidth) < 0.5
        const absoluteWidthsToUse = cellWidthsAreValidForThisQuery
                                    ? this._cellWidths
                                    : [nil].arrayByRepeating(this.cells.length)
        
        const widthFrames = bounds.rectanglesBySplittingWidth(
            this.cellWeights,
            this.padding,
            this._effectiveCellWidthsForBounds(bounds, absoluteWidthsToUse)
        )
        
        // noinspection UnnecessaryLocalVariableJS
        const result = this.cells.map((cell, index) => {
                if (widthFrames[index].width < 0) {
                    return 0
                }
                return cell.intrinsicContentHeight(widthFrames[index].width)
            }
        ).max()
        
        return result
        
    }

    private _effectiveCellWidthsForBounds(bounds: UIRectangle, cellWidths: number[]): number[] {
        const cellCount = this.cells.length
        if (!cellCount || !this.cellMinimumWidths.length) {
            return cellWidths
        }

        const absoluteWidths = cellWidths.length >= cellCount
                               ? cellWidths.copy()
                               : [nil].arrayByRepeating(cellCount)
        const weights = this.cellWeights.length >= cellCount
                        ? this.cellWeights
                        : [1].arrayByRepeating(cellCount)
        const minimumWidths = this.cellMinimumWidths.length >= cellCount
                              ? this.cellMinimumWidths
                              : [0].arrayByRepeating(cellCount)

        let fixedWidth = 0
        let relativeMinimumWidth = 0
        let relativeWeight = 0
        const relativeIndices: number[] = []

        for (let index = 0; index < cellCount; index++) {
            if (IS_NOT_NIL(absoluteWidths[index])) {
                fixedWidth += Math.max(0, absoluteWidths[index] as number)
                continue
            }

            relativeIndices.push(index)
            relativeMinimumWidth += Math.max(0, minimumWidths[index] ?? 0)
            relativeWeight += Math.max(0, weights[index] ?? 1)
        }

        if (!relativeIndices.length) {
            return absoluteWidths
        }

        const availableWidth = Math.max(
            0,
            bounds.width - this.padding * Math.max(0, cellCount - 1) - fixedWidth
        )
        const extraWidth = Math.max(0, availableWidth - relativeMinimumWidth)
        const fallbackExtraWidth = relativeIndices.length ? extraWidth / relativeIndices.length : 0

        relativeIndices.forEach(index => {
            const weight = Math.max(0, weights[index] ?? 1)
            const weightedExtraWidth = relativeWeight > 0
                                       ? extraWidth * weight / relativeWeight
                                       : fallbackExtraWidth
            absoluteWidths[index] = Math.max(0, minimumWidths[index] ?? 0) + weightedExtraWidth
        })

        return absoluteWidths
    }
    
}
